import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Building2, Award, CheckCircle } from 'lucide-react';
import { companyInfo } from '../mockData';

const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative pt-32 pb-20 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-yellow-300/20 to-yellow-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-gray-400/10 to-gray-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-400 text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-lg">
              <Award size={18} />
              {companyInfo.yearsExperience} ans d'excellence dans le BTP
            </div>

            {/* Heading */}
            <div className="space-y-6">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 leading-tight">
                Bâtissons ensemble
                <span className="block mt-2 bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">
                  vos projets d'avenir
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {companyInfo.description}
              </p>
            </div>

            {/* Key points */}
            <div className="space-y-3">
              {[
                'Qualité et expertise reconnues',
                'Respect des délais garantis',
                'Accompagnement personnalisé'
              ].map((point, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <span className="text-gray-700 font-medium">{point}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-blue-700 hover:to-blue-600 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl group transition-all"
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button
                onClick={() => scrollToSection('projects')}
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 hover:border-yellow-600 hover:text-yellow-600 hover:bg-yellow-50 px-8 py-6 text-lg transition-all"
              >
                Nos réalisations
              </Button>
            </div>
          </div>

          {/* Right content - Image grid with floating cards */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform hover:scale-105 transition-transform">
                  <img
                    src="https://customer-assets.emergentagent.com/job_sogi-construction/artifacts/vqapriuz_DSC_0034.JPG"
                    alt="Abdoulaye DIARRA - Fondateur SOGI"
                    className="w-full h-full object-contain bg-white"
                  />
                </div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform hover:scale-105 transition-transform">
                  <img
                    src="https://customer-assets.emergentagent.com/job_sogi-construction-2/artifacts/hbdpf2ly_f3ef5b6f-dd66-41c3-ae69-d42056eec254.jpg"
                    alt="Projet SOGI"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform hover:scale-105 transition-transform">
                  <img
                    src="https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc2NTAwNTY1Nnww&ixlib=rb-4.1.0&q=85"
                    alt="Construction site"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform hover:scale-105 transition-transform">
                  <img
                    src="https://customer-assets.emergentagent.com/job_sogi-construction/artifacts/luyhriti_PHOTO_7%20-%20Photo.jpg"
                    alt="Projet SOGI - Immeuble résidentiel moderne"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Floating stats card - centered on full width */}
        <div className="relative -mt-12 flex justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 w-full max-w-2xl mx-6">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">+100</div>
                <div className="text-xs text-gray-600 mt-1">Projets réalisés</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">25</div>
                <div className="text-xs text-gray-600 mt-1">Ans d'expérience</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">+100</div>
                <div className="text-xs text-gray-600 mt-1">Clients satisfaits</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
